import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, AlertCircle, User, Play } from 'lucide-react';
import taskService from '../../../services/task.service';

const PersonalTasksWidget = ({ userRole, dashboardView = 'personal' }) => {
  const [taskData, setTaskData] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    total: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPersonalTasksData();
  }, [userRole]);

  const fetchPersonalTasksData = async () => {
    try {
      setLoading(true);

      // Fetch all types of personal tasks (daily, weekly, monthly, quarterly, annual)
      const taskTypes = ['daily', 'weekly', 'monthly', 'quarterly', 'annual'];
      let allTasks = [];

      for (const type of taskTypes) {
        try {
          const tasksResponse = await taskService.getTasks({ type });
          if (tasksResponse && tasksResponse.data) {
            // Filter out team tasks (same logic as TaskBox component)
            const personalTasks = tasksResponse.data.filter(task =>
              !task.team_task_id && !task.is_team_task
            );
            allTasks.push(...personalTasks);
          }
        } catch (error) {
          console.warn(`Error fetching ${type} tasks:`, error);
        }
      }

      if (allTasks.length > 0) {
        // Count tasks by status
        const counts = {
          pending: 0,
          inProgress: 0,
          completed: 0,
          overdue: 0
        };

        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to start of day

        allTasks.forEach(task => {
          // Check if task is overdue
          const isOverdue = task.due_date && new Date(task.due_date) < today && task.status !== 'completed';

          if (isOverdue) {
            counts.overdue++;
          } else {
            switch (task.status) {
              case 'pending':
                counts.pending++;
                break;
              case 'in_progress':
                counts.inProgress++;
                break;
              case 'completed':
                counts.completed++;
                break;
              default:
                counts.pending++; // Default to pending for unknown statuses
            }
          }
        });

        counts.total = allTasks.length;

        setTaskData(counts);
        console.log('📊 Personal Tasks Widget - Real-time counts:', counts);
      } else {
        // If no tasks data, set zeros
        setTaskData({
          pending: 0,
          inProgress: 0,
          completed: 0,
          overdue: 0,
          total: 0
        });
      }
    } catch (error) {
      console.error('Error fetching personal tasks data:', error);
      // Set zeros on error to avoid showing random numbers
      setTaskData({
        pending: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        total: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWidgetClick = () => {
    // Navigate to appropriate TaskBox personal view based on user role
    switch (userRole) {
      case 'admin':
        navigate('/admin/taskbox?view=personal');
        break;
      case 'manager':
        navigate('/manager/taskbox?view=personal');
        break;
      case 'employee':
        navigate('/employee/taskbox?view=personal');
        break;
      default:
        navigate('/tasks/personal');
    }
  };

  const getTaskIcon = (type) => {
    switch (type) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'inProgress':
        return <Play className="w-4 h-4 text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <User className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTaskLabel = (type) => {
    switch (type) {
      case 'pending':
        return 'Pending';
      case 'inProgress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'overdue':
        return 'Overdue';
      default:
        return 'Tasks';
    }
  };

  const taskItems = [
    { type: 'pending', count: taskData.pending, color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { type: 'inProgress', count: taskData.inProgress, color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { type: 'completed', count: taskData.completed, color: 'bg-green-100 text-green-800 border-green-200' },
    { type: 'overdue', count: taskData.overdue, color: 'bg-red-100 text-red-800 border-red-200' }
  ];

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 h-96 cursor-pointer group"
      onClick={handleWidgetClick}
    >
      <div className="p-5 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
              Personal Tasks
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Your task overview
            </p>
          </div>
          <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg group-hover:bg-blue-200 dark:group-hover:bg-blue-800 transition-colors">
            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        {/* Total Tasks */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Total Tasks</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{taskData.total}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-1 rounded-lg shadow-sm">
              <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Task Status Items - 2x2 Grid */}
        <div className="grid grid-cols-2 gap-2 flex-1">
          {taskItems.map((item) => (
            <div
              key={item.type}
              className={`flex flex-col items-center justify-center p-2 rounded-lg border ${item.color} hover:shadow-sm transition-all`}
            >
              <div className="flex items-center space-x-1 mb-1">
                {getTaskIcon(item.type)}
                <span className="text-xs font-medium text-center">{getTaskLabel(item.type)}</span>
              </div>
              <span className="text-lg font-bold">{item.count}</span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Click to view all tasks</span>
            <svg className="w-3 h-3 text-gray-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalTasksWidget;